import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress, 
  Container, 
  Divider, 
  Grid, 
  TextField, 
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  useTheme,
  useMediaQuery,
  Alert
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import shipmentService from '../../services/shipmentService';
import { usersApi } from '../../services/api';
import { getDrivers } from '../../services/driverService';
import zoneService from '../../services/zoneService';
import { CreateShipmentDto, ShipmentWithTracking } from '../../types/shipment';
import { Zone } from '../../types/zone';

type EditShipmentFormData = {
  origin: string;
  destination: string;
  weight: string | number;
  customer_id: number | undefined;
  driver_id: string | null;  // Changed to string to match driver IDs
  zone_id: number | null;
  notes: string | null;
};

const schema = yup.object().shape({
  origin: yup
    .string()
    .required('Origin is required')
    .max(255, 'Origin must be at most 255 characters'),
  destination: yup
    .string()
    .required('Destination is required')
    .max(255, 'Destination must be at most 255 characters'),
  weight: yup
    .number()
    .typeError('Weight must be a number')
    .required('Weight is required')
    .positive('Weight must be greater than 0')
    .max(1000, 'Weight must be at most 1000'),
  customer_id: yup
    .number()
    .typeError('Customer must be a number')
    .required('Customer is required')
    .integer('Customer must be an integer'),
  driver_id: yup
    .string()
    .nullable()
    .transform((value) => (value === '' || value === undefined ? null : value)),
  zone_id: yup
    .number()
    .nullable()
    .transform((value) => (isNaN(value) ? null : value)),
  notes: yup
    .string()
    .nullable()
    .max(1000, 'Notes must be at most 1000 characters'),
});

const EditShipmentForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackingNumber } = useParams<{ trackingNumber: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  
  console.log('EditShipmentForm - Component initialized with trackingNumber:', trackingNumber);
  
  const [loading, setLoading] = useState<boolean>(true);
  const [customers, setCustomers] = useState<Array<{ id: number; name: string }>>([]);
  const [drivers, setDrivers] = useState<Array<{ id: string; name: string }>>([]);
  const [zones, setZones] = useState<Array<{ id: number; name: string }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [shipment, setShipment] = useState<ShipmentWithTracking | null>(null);

  const { control, handleSubmit, formState: { errors, isDirty, isValid }, setValue, reset } = useForm<EditShipmentFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      origin: '',
      destination: '',
      weight: '',
      customer_id: undefined,
      driver_id: null,
      zone_id: null,
      notes: null
    },
    mode: 'onChange'
  });

  // Fetch shipment data and form options
  useEffect(() => {
    const fetchData = async () => {
      if (!trackingNumber) {
        setError('Tracking number is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        // Fetch shipment data and form options in parallel
        const [shipmentData, customersData, driversData, zonesData] = await Promise.all([
          shipmentService.getShipment(trackingNumber),
          usersApi.getAll(),
          getDrivers(),
          zoneService.getZones(),
        ]);

        setShipment(shipmentData);
        setCustomers(customersData.map(c => ({ id: c.id, name: c.name || '' })));
        setDrivers(driversData.map(d => ({ id: String(d.id), name: d.name || '' })));
        
        // Use real zones data from API
        const zonesArray: Zone[] = Array.isArray(zonesData) ? zonesData : (zonesData as any)?.data || [];
        setZones(zonesArray.map((z: Zone) => ({ id: z.id, name: z.name || '' })));

        // Populate form with existing shipment data
        const currentDriverId = shipmentData.driverId || shipmentData.driver_id;
        console.log('Current shipment driver_id:', currentDriverId, 'Available drivers:', driversData.map(d => ({ id: d.id, name: d.name })));
        
        reset({
          origin: shipmentData.origin || '',
          destination: shipmentData.destination || '',
          weight: shipmentData.weight || '',
          customer_id: shipmentData.customerId || shipmentData.customer_id,
          driver_id: currentDriverId ? String(currentDriverId) : null,
          zone_id: shipmentData.zoneId || shipmentData.zone_id || null,
          notes: shipmentData.notes || null
        });

      } catch (error: any) {
        console.error('Error fetching shipment data:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load shipment data';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [trackingNumber, reset, enqueueSnackbar]);

  const onSubmit: SubmitHandler<EditShipmentFormData> = async (data) => {
    console.log('🚀 [EditShipmentForm] Form submission started');
    console.log('📋 [EditShipmentForm] Raw form data received:', data);
    console.log('🔍 [EditShipmentForm] trackingNumber from params:', trackingNumber);
    
    if (!trackingNumber) {
      console.error('❌ [EditShipmentForm] No tracking number found in params');
      enqueueSnackbar('Tracking number is required', { variant: 'error' });
      return;
    }

    try {
      console.log('⏳ [EditShipmentForm] Setting isSubmitting to true');
      setIsSubmitting(true);
      setError('');
      
      // Log current form values for debugging
      console.log('📊 [EditShipmentForm] Current form field values:');
      console.log('  - origin:', data.origin);
      console.log('  - destination:', data.destination);
      console.log('  - weight:', data.weight, '(type:', typeof data.weight, ')');
      console.log('  - customer_id:', data.customer_id, '(type:', typeof data.customer_id, ')');
      console.log('  - driver_id:', data.driver_id, '(type:', typeof data.driver_id, ')');
      console.log('  - zone_id:', data.zone_id, '(type:', typeof data.zone_id, ')');
      console.log('  - notes:', data.notes);
      
      // Prepare the shipment data with detailed logging
      console.log('🔄 [EditShipmentForm] Converting form data for API...');
      const shipmentData: Partial<CreateShipmentDto> = {
        ...data,
        weight: Number(data.weight),
        customer_id: Number(data.customer_id),
        driver_id: data.driver_id ? Number(data.driver_id) : null,
        zone_id: data.zone_id ? Number(data.zone_id) : null,
        notes: data.notes || null
      };
      
      console.log('✅ [EditShipmentForm] Converted shipment data for API:');
      console.log('  - origin:', shipmentData.origin);
      console.log('  - destination:', shipmentData.destination);
      console.log('  - weight:', shipmentData.weight, '(type:', typeof shipmentData.weight, ')');
      console.log('  - customer_id:', shipmentData.customer_id, '(type:', typeof shipmentData.customer_id, ')');
      console.log('  - driver_id:', shipmentData.driver_id, '(type:', typeof shipmentData.driver_id, ')');
      console.log('  - zone_id:', shipmentData.zone_id, '(type:', typeof shipmentData.zone_id, ')');
      console.log('  - notes:', shipmentData.notes);

      console.log('🌐 [EditShipmentForm] About to call shipmentService.updateShipment...');
      console.log('🎯 [EditShipmentForm] API call parameters:');
      console.log('  - trackingNumber:', trackingNumber);
      console.log('  - shipmentData:', JSON.stringify(shipmentData, null, 2));
      
      const result = await shipmentService.updateShipment(trackingNumber, shipmentData);
      
      console.log('✅ [EditShipmentForm] API call successful! Result:', result);
      console.log('🎉 [EditShipmentForm] Shipment updated successfully');
      
      enqueueSnackbar(t('shipments.messages.updated'), { variant: 'success' });
      
      console.log('🔄 [EditShipmentForm] Navigating back to /shipments');
      navigate('/shipments');
      
    } catch (error: any) {
      console.error('💥 [EditShipmentForm] Error during shipment update:', error);
      console.error('💥 [EditShipmentForm] Error details:');
      console.error('  - message:', error.message);
      console.error('  - response:', error.response);
      console.error('  - response.data:', error.response?.data);
      console.error('  - response.status:', error.response?.status);
      console.error('  - response.statusText:', error.response?.statusText);
      
      const errorMessage = error.response?.data?.message || error.message || t('shipments.messages.updateError');
      console.error('📝 [EditShipmentForm] Final error message:', errorMessage);
      
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      console.log('🏁 [EditShipmentForm] Setting isSubmitting to false');
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !shipment) {
    return (
      <Container maxWidth="md">
        <Box mt={4}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/shipments')}
          >
            {t('common.back')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box mt={4}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/shipments')}
            sx={{ mr: 2 }}
          >
            {t('common.back')}
          </Button>
          <Typography variant="h4" component="h1">
            {t('shipments.editShipment')} - {trackingNumber}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit, (errors) => {
              console.error('🚨 [EditShipmentForm] Form validation failed:', errors);
              console.error('🚨 [EditShipmentForm] Validation errors details:');
              Object.keys(errors).forEach(field => {
                const fieldKey = field as keyof typeof errors;
                console.error(`  - ${field}:`, errors[fieldKey]?.message);
              });
              // Also show a user-friendly error message
              enqueueSnackbar('Please fix the form errors before submitting', { variant: 'error' });
            })}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="origin"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={t('shipments.fields.origin')}
                        variant="outlined"
                        margin="normal"
                        error={!!errors.origin}
                        helperText={errors.origin?.message}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="destination"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={t('shipments.fields.destination')}
                        variant="outlined"
                        margin="normal"
                        error={!!errors.destination}
                        helperText={errors.destination?.message}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="weight"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="number"
                        label={t('shipments.fields.weight')}
                        variant="outlined"
                        margin="normal"
                        error={!!errors.weight}
                        helperText={errors.weight?.message}
                        required
                        inputProps={{ min: 0, step: 0.1 }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="customer_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth margin="normal" error={!!errors.customer_id}>
                        <InputLabel id="customer-label">
                          {t('shipments.fields.customer')} *
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="customer-label"
                          label={`${t('shipments.fields.customer')} *`}
                          onChange={(e) => field.onChange(e.target.value)}
                          value={field.value || ''}
                        >
                          {customers.map((customer) => (
                            <MenuItem key={customer.id} value={customer.id}>
                              {customer.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.customer_id && (
                          <FormHelperText>{errors.customer_id.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="driver_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="driver-label">
                          {t('shipments.fields.driver')} ({t('common.optional')})
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="driver-label"
                          label={`${t('shipments.fields.driver')} (${t('common.optional')})`}
                          onChange={(e) => field.onChange(e.target.value)}
                          value={field.value || ''}
                        >
                          <MenuItem value="">
                            <em>{t('common.none')}</em>
                          </MenuItem>
                          {drivers.map((driver) => (
                            <MenuItem key={driver.id} value={driver.id}>
                              {driver.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="zone_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="zone-label">
                          {t('shipments.fields.zone')} ({t('common.optional')})
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="zone-label"
                          label={`${t('shipments.fields.zone')} (${t('common.optional')})`}
                          onChange={(e) => field.onChange(e.target.value)}
                          value={field.value || ''}
                        >
                          <MenuItem value="">
                            <em>{t('common.none')}</em>
                          </MenuItem>
                          {zones.map((zone) => (
                            <MenuItem key={zone.id} value={zone.id}>
                              {zone.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={4}
                        label={t('shipments.fields.notes')}
                        variant="outlined"
                        margin="normal"
                        placeholder={t('shipments.notesPlaceholder')}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/shipments')}
                      disabled={isSubmitting}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
                      disabled={isSubmitting}
                      onClick={() => {
                        console.log('💆 [EditShipmentForm] Submit button clicked!');
                        console.log('📊 [EditShipmentForm] Current form state:');
                        console.log('  - isSubmitting:', isSubmitting);
                        console.log('  - form errors:', errors);
                        console.log('  - form isDirty:', isDirty);
                        console.log('  - form isValid:', isValid);
                      }}
                    >
                      {isSubmitting ? t('common.updating') : t('common.update')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default EditShipmentForm;
